import { Spinner } from '@consigli/facade';
import {
  useGetBlobContractorsQuery,
  usePackage,
  usePackageId,
  useProject,
  useProjectId,
} from '@consigli/hooks';
import { DocumentPackageType, JobType } from '@consigli/types';
import { useMemo, useState } from 'react';

import { useRegisterBreadcrumb } from '@/components';
import { PageHeader } from '@/components/page-header';
import { Tabs } from '@/components/tabs';
import { ServiceType } from '@/utils/types';
import { useTabParam } from '@/utils/use-tab-param';

import { BidComparisonTab } from './bid-comparison/bid-comparison-tab';
import { DocumentsTab } from './documents/documents-tab';
import { UploadDocumentForm } from './documents/form/upload-document-form';
import { DownloadAllDocumentsButton } from './download-all-documents-button';
import { CreateFindingForm } from './findings/create-finding-form/create-finding-form';
import { DeleteAllFindingsButton } from './findings/delete-all-findings-button';
import { DeleteNonRelevantFindingsButton } from './findings/delete-non-relevant-findings-button';
import { ExportFindingsButton } from './findings/export-findings-button';
import { FindingsTab } from './findings/findings-tab';
import { emptyFindingsFilterState, FindingsFilterState } from './findings/types';
import { PackageVisibilityButton } from './package-visibility-button';
import { PipelinesTab } from './pipelines/pipelines-tab';
import { StartPipelineMenu } from './pipelines/start-pipeline-menu';
import { ReportsTab } from './reports';
import { SummaryTab } from './summary/summary-tab';
import { DeleteAllVersionComparisonsButton } from './version-compare/delete-all-version-comparisons-button';
import { VersionCompareTab } from './version-compare/version-compare-tab';

type TabValue = 'summary' | 'documents' | 'reports' | 'findings' | 'pipelines' | 'bid-comparison';

export const DocumentPackagePage = () => {
  const packageId = usePackageId();
  const projectId = useProjectId();
  const [findingsFilters, setFindingsFilters] =
    useState<FindingsFilterState>(emptyFindingsFilterState);
  const { project } = useProject(projectId);
  const { pkg, isLoading } = usePackage(projectId, packageId);

  useRegisterBreadcrumb(projectId, project?.name);
  useRegisterBreadcrumb(packageId, pkg?.name);

  const isSiteDueDiligence = pkg?.type === DocumentPackageType.SITE_DUE_DILIGENCE;
  const isTenderDocuments = pkg?.type === DocumentPackageType.TENDER_DOCUMENTS;
  const isLuminaireSchedule = pkg?.type === DocumentPackageType.LUMINAIRE_SCHEDULE;
  const isVersionCompare = pkg?.type === DocumentPackageType.VERSION_COMPARE;
  const isBidReview = pkg?.type === DocumentPackageType.OFFER;

  const { data: contractors = [] } = useGetBlobContractorsQuery(
    { projectId, packageId },
    { skip: !isBidReview },
  );
  const showBidComparison = isBidReview && contractors.length > 0;

  // Which tabs this package type actually renders. Kept in step with the tabs
  // built below, and used to reject a `?tab=` value this package has no tab for.
  const tabValues = useMemo<TabValue[]>(() => {
    const values: TabValue[] = [];
    if (isTenderDocuments) values.push('summary');
    values.push('documents');
    if (isSiteDueDiligence || isLuminaireSchedule) values.push('reports');
    if (showBidComparison) values.push('bid-comparison');
    values.push('findings', 'pipelines');
    return values;
  }, [isTenderDocuments, isSiteDueDiligence, isLuminaireSchedule, showBidComparison]);

  const [selectedTab, setSelectedTab] = useTabParam<TabValue>('documents', tabValues);

  const tabs = useMemo(() => {
    const baseTabs: { value: TabValue; label: string; content: React.ReactNode }[] = [];

    if (isTenderDocuments) {
      baseTabs.push({
        value: 'summary',
        label: 'Summary',
        content: (
          <SummaryTab
            projectId={projectId}
            packageId={packageId}
            onNavigateToFindings={() => setSelectedTab('findings')}
          />
        ),
      });
    }

    baseTabs.push({
      value: 'documents',
      label: 'Documents',
      content: (
        <DocumentsTab
          serviceId={packageId}
          projectId={projectId}
          serviceType={ServiceType.DOCUMENT_PACKAGE}
          isBidReview={isBidReview}
        />
      ),
    });

    // Add Reports tab for Site Due Diligence packages
    if (isSiteDueDiligence) {
      baseTabs.push({
        value: 'reports',
        label: 'Reports',
        content: <ReportsTab />,
      });
    }

    // Add Results tab for Luminaire Schedule packages
    if (isLuminaireSchedule) {
      baseTabs.push({
        value: 'reports',
        label: 'Results',
        content: <ReportsTab />,
      });
    }

    if (showBidComparison) {
      baseTabs.push({
        value: 'bid-comparison',
        label: 'Bid Comparison',
        content: <BidComparisonTab projectId={projectId} packageId={packageId} />,
      });
    }

    baseTabs.push(
      {
        value: 'findings',
        label: 'Findings',
        content: isVersionCompare ? (
          <VersionCompareTab projectId={projectId} packageId={packageId} />
        ) : (
          <FindingsTab
            documentPackage={pkg!}
            filters={findingsFilters}
            onFiltersChange={setFindingsFilters}
          />
        ),
      },
      {
        value: 'pipelines',
        label: 'Pipelines',
        content: <PipelinesTab projectId={projectId} packageId={packageId} />,
      },
    );

    return baseTabs;
  }, [
    packageId,
    projectId,
    isBidReview,
    showBidComparison,
    isSiteDueDiligence,
    isTenderDocuments,
    isLuminaireSchedule,
    isVersionCompare,
    pkg,
    findingsFilters,
    setSelectedTab,
  ]);

  if (isLoading) return <Spinner />;

  if (!pkg) return <div>Package not found</div>;

  return (
    <div>
      <PageHeader title="Document Package" subtitle={pkg.name}>
        <div className="flex gap-2">
          <PackageVisibilityButton />
          {selectedTab === 'documents' && (
            <>
              <DownloadAllDocumentsButton />
              <UploadDocumentForm />
            </>
          )}
          {selectedTab === 'findings' &&
            (isVersionCompare ? (
              <DeleteAllVersionComparisonsButton />
            ) : (
              <>
                <DeleteNonRelevantFindingsButton filters={findingsFilters} />
                <DeleteAllFindingsButton filters={findingsFilters} />
                <ExportFindingsButton />
                <CreateFindingForm projectId={projectId} packageId={packageId} />
              </>
            ))}
          {selectedTab === 'pipelines' && (
            <StartPipelineMenu
              projectId={projectId}
              packageId={packageId}
              jobType={JobType.DOCUMENT_PACKAGE}
              documentPackageType={pkg.type}
            />
          )}
        </div>
      </PageHeader>
      <Tabs
        value={selectedTab}
        onValueChange={(value) => setSelectedTab(value as TabValue)}
        tabs={tabs}
      />
    </div>
  );
};
